import { agentforgeApiQuery } from '../index';
import type { ChatSession, ChatMessage } from '@/types/mission-control';

export const chatApi = agentforgeApiQuery.injectEndpoints({
  endpoints: (builder) => ({
    // List sessions for an agent
    listSessions: builder.query<ChatSession[], { agentId?: string }>({
      query: ({ agentId } = {}) => ({
        url: '/sessions',
        params: { agent_id: agentId },
      }),
      transformResponse: (response: { sessions: ChatSession[] }) =>
        response.sessions || [],
      providesTags: (_result, _err, arg) =>
        arg?.agentId
          ? [{ type: 'MCSessions' as const, id: arg.agentId }]
          : ['MCSessions'],
    }),

    // Create a new session with an agent
    createSession: builder.mutation<
      ChatSession,
      { agent_id: string; channel_type?: string; title?: string }
    >({
      query: (body) => ({
        url: '/sessions',
        method: 'post',
        data: body,
      }),
      transformResponse: (response: { session: ChatSession }) =>
        response.session,
      invalidatesTags: (_result, _err, arg) => [
        { type: 'MCSessions' as const, id: arg.agent_id },
      ],
    }),

    // Get message history for a session
    getMessages: builder.query<
      { data: ChatMessage[]; total: number },
      { sessionId: string; limit?: number; offset?: number }
    >({
      query: ({ sessionId, limit = 50, offset = 0 }) => ({
        url: `/sessions/${sessionId}/messages`,
        params: { limit, offset },
      }),
      transformResponse: (response: { messages: ChatMessage[] }) => {
        const messages = (response.messages || []).map((msg) => {
          // Normalize tool_calls from DB format ({id, name, input}) to frontend format
          if (msg.tool_calls && msg.tool_calls.length > 0) {
            msg.tool_calls = msg.tool_calls.map((tc: any) => ({
              tool_call_id: tc.tool_call_id || tc.id || '',
              tool_name: tc.tool_name || tc.name || '',
              input: tc.input,
              output: tc.output,
              error: tc.error,
              duration_ms: tc.duration_ms,
            }));
          }
          return msg;
        });
        return { data: messages, total: messages.length };
      },
      providesTags: (_result, _err, arg) => [
        { type: 'MCMessages' as const, id: arg.sessionId },
      ],
    }),

    // Delete a session
    deleteSession: builder.mutation<
      void,
      { sessionId: string; agentId?: string }
    >({
      query: ({ sessionId }) => ({
        url: `/sessions/${sessionId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, _err, arg) =>
        arg?.agentId
          ? [{ type: 'MCSessions' as const, id: arg.agentId }]
          : ['MCSessions'],
    }),

    // Update a session (rename)
    updateSession: builder.mutation<
      ChatSession,
      { sessionId: string; title: string }
    >({
      query: ({ sessionId, title }) => ({
        url: `/sessions/${sessionId}`,
        method: 'put',
        data: { title },
      }),
      transformResponse: (response: { session: ChatSession }) =>
        response.session,
      invalidatesTags: ['MCSessions'],
    }),

    // Send a message (triggers agent workflow, response comes via WS)
    sendMessage: builder.mutation<
      {
        workflow_id: string;
        run_id: string;
        session_id: string;
        status: string;
      },
      { sessionId: string; message: string; timezone?: string }
    >({
      query: ({ sessionId, message, timezone }) => ({
        url: `/sessions/${sessionId}/messages`,
        method: 'post',
        data: { message, timezone },
      }),
    }),

    // Execute a deterministic action (no LLM, triggers ActionWorkflow)
    executeAction: builder.mutation<
      { workflow_id: string; action: string; status: string },
      { sessionId: string; action: string; params: Record<string, any> }
    >({
      query: ({ sessionId, action, params }) => ({
        url: `/sessions/${sessionId}/actions`,
        method: 'post',
        data: { action, params },
      }),
    }),
  }),
});

export const {
  useListSessionsQuery,
  useCreateSessionMutation,
  useUpdateSessionMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useDeleteSessionMutation,
  useExecuteActionMutation,
} = chatApi;
